import type { TipoAlvoImpacto } from './alvosImpacto'
import type { TipoColisao } from './timelineCinematica'
import type { TonusOcupante } from './tonusOcupante'

export type ClassificacaoDeltaV = 'baixo' | 'moderado' | 'grave'

export interface MetricasDeltaV {
  deltaVKmh: number
  deltaVMs: number
  classificacao: ClassificacaoDeltaV
  rotuloClassificacao: string
  escalaAis: string
  probabilidadeLesaoGravePct: number
  descricaoClinica: string
  criterioTransportePhtls: boolean
  justificativaEngenharia: string
}

export interface PontoCurvaG {
  tempoMs: number
  tempoNormalizado: number
  g: number
}

export interface PulsoAceleracao {
  gAtual: number
  gPico: number
  tempoPicoMs: number
  duracaoTotalMs: number
  curva: readonly PontoCurvaG[]
  interpretacao: string
}

export interface AvaliacaoSindromeCinto {
  temSubmarining: boolean
  temSinalDoCinto: boolean
  descricao: string
  lesoesAssociadas: readonly string[]
  riscoFraturaChance: boolean
  alerta: string
}

export interface ItemXABCDE {
  letra: 'X' | 'A' | 'B' | 'C' | 'D' | 'E'
  nome: string
  prioridade: 'critica' | 'alta' | 'moderada'
  achadoCinematico: string
  suspeitaClinica: string
  condutaCbmmg: string
}

export type TrajetoriaFrontal = 'upAndOver' | 'downAndUnder'

/**
 * Calcula a variação de velocidade líquida (Delta-V) sofrida pelo veículo durante o pulso de impacto.
 * O Delta-V é a principal métrica internacional (PHTLS / IIHS / EuroNCAP) para prever
 * a gravidade do trauma nos ocupantes.
 */
export function calcularDeltaV(
  tipoColisao: TipoColisao,
  alvo: TipoAlvoImpacto,
  velocidadeKmh: number,
  opcoes?: {
    ehEV?: boolean
    ehBlindado?: boolean
  },
): MetricasDeltaV {
  // Fator de transferência de momento baseado no alvo e tipo de choque
  let fatorAbsorcao = 0.70

  switch (tipoColisao) {
    case 'frontal':
      fatorAbsorcao = alvo === 'poste' ? 0.95 : alvo === 'muro' ? 0.90 : 0.75
      break
    case 'lateral':
    case 'lateralComCapotamento':
      fatorAbsorcao = alvo === 'poste' ? 0.85 : 0.70
      break
    case 'traseira':
      fatorAbsorcao = 0.55
      break
    case 'frontalComTraseira':
      fatorAbsorcao = 0.85
      break
    case 'tombamento':
    case 'capotamento':
      fatorAbsorcao = 0.45
      break
    case 'abalroamento':
      // raspagem: só a pequena componente que fecha uma lateral sobre a outra vira ΔV
      fatorAbsorcao = 0.2
      break
    case 'lateralContraObjeto':
      fatorAbsorcao = alvo === 'poste' ? 0.95 : 0.9
      break
  }

  // Modificadores tecnológicos veiculares (Pilar 5)
  if (opcoes?.ehEV) {
    // Veículos Elétricos: maior massa (+25-30%) reduz o Delta-V relativo em colisão contra outros carros,
    // mas rigidez da caixa da bateria eleva a rigidez em impactos contra postes/obstáculos rígidos
    fatorAbsorcao = alvo === 'poste' ? fatorAbsorcao * 1.05 : fatorAbsorcao * 0.88
  }

  if (opcoes?.ehBlindado) {
    // Veículo blindado não possui colapsabilidade programada convencional:
    // quase toda a energia cinética é convertida em desaceleração seca
    fatorAbsorcao = Math.min(1.15, fatorAbsorcao * 1.25)
  }

  const deltaVKmh = Math.round(velocidadeKmh * fatorAbsorcao)
  const deltaVMs = Number((deltaVKmh / 3.6).toFixed(1))

  let classificacao: ClassificacaoDeltaV = 'baixo'
  let rotuloClassificacao = 'Baixa Energia (Baixo Risco)'
  let escalaAis = 'AIS 1 (Lesões Leves)'
  let probabilidadeLesaoGravePct = 2
  let descricaoClinica = 'Risco menor que 1% de lesão com ameaça imediata à vida. Esperam-se apenas contusões leves, escoriações e mialgia por desaceleração.'
  let criterioTransportePhtls = false
  let justificativaEngenharia = 'A deformação progressiva do veículo e os sistemas de retenção conseguiram absorver e atenuar a quase totalidade do momento cinético.'

  if (deltaVKmh >= 40) {
    classificacao = 'grave'
    rotuloClassificacao = 'Alta Energia / Risco Vital Severo'
    escalaAis = 'AIS 4-6 (Grave a Crítico)'
    probabilidadeLesaoGravePct = Math.min(95, Math.round(35 + (deltaVKmh - 40) * 1.5))
    descricaoClinica = 'Alta probabilidade de trauma toracoabdominal grave, lesões por cisalhamento aórtico, contusão pulmonar maciça e TCE com lesão axonal difusa.'
    criterioTransportePhtls = true
    justificativaEngenharia = 'Pulso de desaceleração violento que excede a capacidade de absorção elasto-plástica da carroceria, transmitindo choque severo ao habitáculo.'
  } else if (deltaVKmh >= 20) {
    classificacao = 'moderado'
    rotuloClassificacao = 'Média Energia (Risco Moderado)'
    escalaAis = 'AIS 2-3 (Lesões Moderadas a Graves)'
    probabilidadeLesaoGravePct = Math.round(10 + (deltaVKmh - 20) * 1.2)
    descricaoClinica = 'Risco moderado de efeito chicote cervical com dor limitante, fraturas costais ou de extremidades, e contusões viscerais que exigem investigação em centro de trauma.'
    criterioTransportePhtls = deltaVKmh >= 35
    justificativaEngenharia = 'Estrutura veicular colapsou parcialmente conforme o projeto de segurança, retendo o habitáculo com esforço significativo nas longarinas.'
  }

  return {
    deltaVKmh,
    deltaVMs,
    classificacao,
    rotuloClassificacao,
    escalaAis,
    probabilidadeLesaoGravePct,
    descricaoClinica,
    criterioTransportePhtls,
    justificativaEngenharia,
  }
}

/**
 * Gera os dados de pulso de aceleração instantânea G(t) em função do tempo da colisão.
 * O pulso real ocorre em 80-140 ms no mundo físico.
 */
export function calcularPulsoAceleracao(
  tipoColisao: TipoColisao,
  velocidadeKmh: number,
  comCinto: boolean,
  tempoNormalizado: number,
  opcoes?: {
    ehEV?: boolean
    ehBlindado?: boolean
    trajetoria?: TrajetoriaFrontal
  },
): PulsoAceleracao {
  const duracaoTotalMs = tipoColisao === 'capotamento' || tipoColisao === 'tombamento' ? 450 : 120
  const tempoMs = tempoNormalizado * duracaoTotalMs

  // Parâmetros de pico de G baseados na retenção e velocidade
  const fatorVelocidade = velocidadeKmh / 50

  let gPico = 0
  let tempoPicoMs = 0
  let interpretacao = ''

  if (comCinto) {
    // Com cinto: o cinto + zonas de colapso amortecem o corpo gradualmente
    // Curva suave com pico de 18G a 28G espalhado em ~50-70 ms
    gPico = Number((22 * fatorVelocidade).toFixed(1))
    if (opcoes?.ehBlindado) gPico = Number((gPico * 1.6).toFixed(1))
    if (opcoes?.ehEV) gPico = Number((gPico * 1.15).toFixed(1))
    tempoPicoMs = 55
    interpretacao = `Desaceleração atenuada em ${gPico}G distribuídos ao longo de ~${duracaoTotalMs} ms graças ao acoplamento do cinto de 3 pontos com limitador de carga e zonas colapsáveis.`
  } else {
    // Sem cinto: o corpo voa livremente com ~0G inicial e depois sofre parada abrupta
    // contra para-brisa, painel ou volante, atingindo picos agulha de 60G a 90G+
    gPico = Number((72 * fatorVelocidade).toFixed(1))
    if (opcoes?.ehBlindado) gPico = Number((gPico * 1.3).toFixed(1))
    tempoPicoMs = 45
    interpretacao = `Pico brutal tipo agulha de ${gPico}G no momento do choque direto contra a cabine. A desaceleração instantânea excede o limite elástico de órgãos sólidos e grandes vasos.`
  }

  // Gera pontos para o mini-gráfico (25 amostras)
  const pontos: PontoCurvaG[] = []
  const numPontos = 25
  for (let i = 0; i <= numPontos; i++) {
    const tNorm = i / numPontos
    const tMs = tNorm * duracaoTotalMs
    let g = 0

    if (comCinto) {
      // Curva em sino (Gaussiana suave)
      const sigma = duracaoTotalMs * 0.22
      const expoente = -Math.pow(tMs - tempoPicoMs, 2) / (2 * Math.pow(sigma, 2))
      g = Number((gPico * Math.exp(expoente)).toFixed(1))
    } else {
      // Sem cinto: quase 0 até o choque (tMs ~ 35), pico estreitíssimo, depois oscilação residual
      if (tMs < 30) {
        g = Number((1.2 * (tMs / 30)).toFixed(1))
      } else {
        const sigma = duracaoTotalMs * 0.08
        const expoente = -Math.pow(tMs - tempoPicoMs, 2) / (2 * Math.pow(sigma, 2))
        g = Number((gPico * Math.exp(expoente)).toFixed(1))
      }
    }
    pontos.push({ tempoMs: Math.round(tMs), tempoNormalizado: Number(tNorm.toFixed(2)), g })
  }

  // Calcula o G no tempo atual
  let gAtual = 0
  if (comCinto) {
    const sigma = duracaoTotalMs * 0.22
    const expoente = -Math.pow(tempoMs - tempoPicoMs, 2) / (2 * Math.pow(sigma, 2))
    gAtual = Number((gPico * Math.exp(expoente)).toFixed(1))
  } else {
    if (tempoMs < 30) {
      gAtual = Number((1.2 * (tempoMs / 30)).toFixed(1))
    } else {
      const sigma = duracaoTotalMs * 0.08
      const expoente = -Math.pow(tempoMs - tempoPicoMs, 2) / (2 * Math.pow(sigma, 2))
      gAtual = Number((gPico * Math.exp(expoente)).toFixed(1))
    }
  }

  return {
    gAtual,
    gPico,
    tempoPicoMs,
    duracaoTotalMs,
    curva: pontos,
    interpretacao,
  }
}

/**
 * Avalia a presença da Síndrome do Cinto de Segurança e do Efeito Submarino (Submarining).
 */
export function avaliarSindromeCinto(
  retencao: string,
  opcoes?: {
    reclinado?: boolean
    subabdominal?: boolean
    velocidadeKmh?: number
  },
): AvaliacaoSindromeCinto {
  const comCinto = retencao === 'comCinto'
  const ehSubabdominal = opcoes?.subabdominal ?? false
  const reclinado = opcoes?.reclinado ?? false
  const velocidade = opcoes?.velocidadeKmh ?? 50

  const temSubmarining = comCinto && (ehSubabdominal || reclinado)
  const temSinalDoCinto = comCinto && velocidade >= 35

  const lesoes: string[] = []
  let descricao = ''
  let alerta = ''

  if (temSubmarining) {
    lesoes.push(
      'Fratura de Chance (distração horizontal de vértebras lombares L1-L3)',
      'Rotura de mesentério com hemorragia intra-abdominal oculta',
      'Perfuração / esmagamento de alças de intestino delgado (duodeno e íleo)',
      'Rotura da aorta abdominal ou veia cava inferior por compressão contra coluna',
    )
    descricao = 'Efeito Submarino Positivo: A pelve deslizou sob a faixa subabdominal, concentrando toda a força de retenção sobre os tecidos moles do abdômen e coluna lombar.'
    alerta = 'ATENÇÃO PHTLS: A presença de postura escorregada ou cinto sem apoio diagonal impõe suspeita obrigatória de Fratura de Chance e lesão visceral oculta, mesmo com abdômen aparentemente indolor inicialmente.'
  } else if (temSinalDoCinto) {
    lesoes.push(
      'Equimose linear oblíqua em tórax e abdômen (Sinal do Cinto)',
      'Fratura isolada de clavícula homolateral à ancoragem',
      'Contusão pulmonar subjacente à faixa torácica',
    )
    descricao = 'Sinal do Cinto de Segurança: A fita conteve a vítima e evitou ejeção fatal, mas deixou marcas contusas pela carga cinética sustentada.'
    alerta = 'O cinto salvou a vida contra a ejeção, mas a equimose cutânea sinaliza que uma grande quantidade de energia cruzou o tórax. Monitorar padrão ventilatório.'
  } else {
    descricao = comCinto
      ? 'Retenção adequada de 3 pontos sem sinais de efeito submarino.'
      : 'Vítima sem cinto: retenção ineficaz, cinemática de corpo livre.'
  }

  return {
    temSubmarining,
    temSinalDoCinto,
    descricao,
    lesoesAssociadas: lesoes,
    riscoFraturaChance: temSubmarining,
    alerta,
  }
}

/**
 * Gera a correlação clínica completa com o mnemônico XABCDE do PHTLS / ATLS
 * baseada no mecanismo de impacto e achados cinemáticos.
 */
export function obterCorrelacaoXABCDE(
  tipoColisao: TipoColisao,
  deltaVKmh: number,
  comCinto: boolean,
  trajetoriaFrontal: TrajetoriaFrontal = 'upAndOver',
  tonus: TonusOcupante = 'alerta',
): readonly ItemXABCDE[] {
  const altaEnergia = deltaVKmh >= 40
  const semCinto = !comCinto

  const itens: ItemXABCDE[] = [
    {
      letra: 'X',
      nome: 'Exsanguinação / Hemorragia Grave Externa e Interna',
      prioridade: altaEnergia || semCinto ? 'critica' : 'moderada',
      achadoCinematico: altaEnergia
        ? `ΔV de ${deltaVKmh} km/h e intrusão estrutural com compressão cavitária.`
        : 'Cinemática de menor energia sem deformação maciça de habitáculo.',
      suspeitaClinica: altaEnergia
        ? (tipoColisao === 'lateral' || tipoColisao === 'lateralComCapotamento')
          ? 'Hemorragia interna maciça por fratura do anel pélvico e laceração esplênica/hepática homolateral.'
          : 'Hemorragia intra-abdominal oculta (baço/fígado/mesentério) e sangramento pélvico por desaceleração rápida.'
        : 'Risco baixo de hemorragia exsanguinante imediata; pesquisar escoriações.',
      condutaCbmmg: altaEnergia
        ? 'Controle imediato de sangramento ativo; cinta pélvica precoce se instabilidade óssea; protocolo de transporte rápido.'
        : 'Exame de busca ativa por sangramentos ocultos e monitoramento de sinais vitais.',
    },
    {
      letra: 'A',
      nome: 'Vias Aéreas e Controle da Coluna Cervical',
      prioridade: semCinto || tonus === 'inconsciente' || tipoColisao === 'traseira' ? 'critica' : 'alta',
      achadoCinematico: semCinto
        ? (trajetoriaFrontal === 'upAndOver'
            ? 'Choque crânio-facial direto contra para-brisa laminado e coluna A.'
            : 'Projeção do tronco contra o volante com hiperextensão forçada da coluna cervical.')
        : (tipoColisao === 'traseira'
            ? 'Efeito chicote por defasagem de aceleração banco-tórax-cabeça.'
            : 'Desaceleração com retenção pelo cinto de 3 pontos.'),
      suspeitaClinica: semCinto && trajetoriaFrontal === 'upAndOver'
        ? 'TCE grave com perda do reflexo de via aérea, fraturas de ossos da face (Le Fort) com sangramento em orofaringe e lesão cervical alta (C1-C3).'
        : tonus === 'inconsciente'
          ? 'Queda da base da língua com obstrução de vias aéreas e estresse ligamentar cervical desprotegido.'
          : 'Edema de partes moles cervicais, estiramento ligamentar ou contratura paravertebral reflexa.',
      condutaCbmmg: 'Estabilização cervical manual imediata da cabeça no eixo neutro antes de qualquer manobra de desencarceramento; cânula orofaríngea se Glasgow rebaixado.',
    },
    {
      letra: 'B',
      nome: 'Boa Respiração e Ventilação',
      prioridade: (tipoColisao === 'frontal' && semCinto) || tipoColisao === 'lateral' || altaEnergia ? 'critica' : 'alta',
      achadoCinematico: tipoColisao === 'lateral'
        ? 'Intrusão da porta e coluna B diretamente contra o hemitórax homolateral.'
        : semCinto
          ? 'Deformação plástica do volante pelo impacto direto do esterno/tórax anterior.'
          : 'Carga sustentada pela faixa torácica do cinto de segurança.',
      suspeitaClinica: tipoColisao === 'lateral'
        ? 'Tórax instável lateral, contusão pulmonar grave homolateral, pneumotórax hipertensivo ou hemotórax maciço.'
        : semCinto
          ? 'Fratura cominutiva de esterno, tórax instável anterior e contusão pulmonar bilateral por choque no volante.'
          : 'Contusão pulmonar segmentar subjacente ao cinto em colisões de alta desaceleração.',
      condutaCbmmg: 'Inspeção e palpação minuciosa de gradil costal; oxigenoterapia em alto fluxo se desconforto ventilatório; descompressão torácica por punção se pneumotórax hipertensivo.',
    },
    {
      letra: 'C',
      nome: 'Circulação e Perfusão com Prevenção de Choque',
      prioridade: altaEnergia ? 'critica' : 'alta',
      achadoCinematico: altaEnergia
        ? `Desaceleração abrupta com Força G elevada superior ao limite do ligamento arterioso.`
        : 'Desaceleração atenuada com perfusão mantida.',
      suspeitaClinica: altaEnergia
        ? 'Suspeita máxima de Rotura Traumática de Aorta Torácica (ligamento arterioso), contusão miocárdica com arritmias e choque obstrutivo/hipovolêmico.'
        : 'Estabilidade hemodinâmica inicial, mas vigiar sinais precoces de choque compensado (taquicardia, palidez cutânea).',
      condutaCbmmg: 'Avaliação de pulsos radiais bilaterais (diferença de pulsos sugere dissecção/rotura aórtica); monitoramento de tempo de enchimento capilar e aquecimento da vítima.',
    },
    {
      letra: 'D',
      nome: 'Disfunção Neurológica e Exame Pupilar',
      prioridade: semCinto || tipoColisao === 'capotamento' ? 'critica' : 'moderada',
      achadoCinematico: semCinto
        ? 'Impacto direto da calota craniana com desaceleração angular e linear.'
        : 'Rotação cervical e desaceleração retida.',
      suspeitaClinica: semCinto
        ? 'TCE moderado/grave com lesão por golpe e contragolpe, hemorragia subaracnóidea traumática, hematoma extradural/subdural e rebaixamento de Glasgow.'
        : 'Concussão cerebral leve a moderada, vertigem pós-traumática ou cefaleia por desaceleração.',
      condutaCbmmg: 'Cálculo objetivo da Escala de Coma de Glasgow (ECG) seriada; avaliação de simetria e reatividade pupilar; prevenção rigorosa de hipóxia e hipotensão secundárias.',
    },
    {
      letra: 'E',
      nome: 'Exposição e Controle de Temperatura / Ambiente',
      prioridade: tipoColisao === 'capotamento' || altaEnergia ? 'alta' : 'moderada',
      achadoCinematico: tipoColisao === 'capotamento'
        ? 'Múltiplos planos de impacto, deformação de teto e quebra generalizada de vidros.'
        : 'Encarceramento mecânico no interior do habitáculo com tempo de resgate estendido.',
      suspeitaClinica: 'Hipotermia induzida por exposição e choque (Tríade da Morte do trauma: coagulopatia, acidose e hipotermia); lesões de extremidades ocultas.',
      condutaCbmmg: 'Exposição seletiva com proteção térmica imediata através de manta aluminizada; blindagem da vítima contra intempéries e estilhaços durante o desencarceramento.',
    },
  ]

  return itens
}
